import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ensureHubSpotScript, createHubSpotForm, HUBSPOT_DARK_CSS } from "@/lib/hubspot";

const ease = [0.16, 1, 0.3, 1] as const;

const HubSpotCareerForm = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!containerRef.current) return;
      containerRef.current.innerHTML = "";
      ensureHubSpotScript(() => {
        if (!containerRef.current) return;
        containerRef.current.innerHTML = `<div id="career-hs-form"></div>`;
        createHubSpotForm({ region: "eu1", portalId: "25538277", formId: "8ede06a1-c6ee-474f-8272-c72740475e5d", target: "#career-hs-form", css: HUBSPOT_DARK_CSS });
      });
    }, 100);
    return () => clearTimeout(timeout);
  }, []);
  return <div ref={containerRef} className="min-h-[200px] [&_.hs-form-frame]:w-full" />;
};

const Careers = () => {
  const { t, i18n } = useTranslation('careers');
  return (
    <div className="min-h-screen section-fade">
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
        <link rel="canonical" href="https://www.cetasol.com/career-at-cetasol-team" />
        <meta property="og:title" content={t('meta.title')} />
        <meta property="og:description" content={t('meta.description')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cetasol.com/career-at-cetasol-team" />
        <meta property="og:image" content="https://www.cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('meta.title')} />
        <meta name="twitter:description" content={t('meta.description')} />
        <meta name="twitter:image" content="https://www.cetasol.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": "Careers", "item": "https://www.cetasol.com/career-at-cetasol-team" }
          ]
        })}</script>
      </Helmet>
      <Header />
      <main className="pt-24 pb-20">
        <section className="container mx-auto px-6 mb-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }} className="max-w-3xl">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-6 leading-[1.1]">{t('heading')}</h1>
            <p className="text-lg text-muted-foreground leading-relaxed">{t('description')}</p>
          </motion.div>
        </section>

        <section className="bg-surface-light py-20">
          <div className="container mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease }} className="grid grid-cols-1 md:grid-cols-3 gap-12 md:divide-x md:divide-border">
              <div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">{t('perk1Title')}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t('perk1Desc')}</p>
              </div>
              <div className="md:pl-12">
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">{t('perk2Title')}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t('perk2Desc')}</p>
              </div>
              <div className="md:pl-12">
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">{t('perk3Title')}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t('perk3Desc')}</p>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-20 section-fade">
          <div className="container mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease }} className="max-w-2xl">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">{t('formHeading')}</h2>
              <p className="text-muted-foreground mb-8">{t('formDescription')}</p>
              <HubSpotCareerForm />
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Careers;
